import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Car, Users, Briefcase, Cog, Loader2, Info, PlugZap, Search, Filter } from 'lucide-react';
import { format, parseISO, isValid, differenceInCalendarDays } from 'date-fns';
import SearchWidget from '../components/SearchWidget';
import IndicativeResultsNotice from '../components/IndicativeResultsNotice';
import CategoryNav from '../components/CategoryNav';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const readSearchFromParams = (params) => {
  const location = params.get('location') || '';
  const pickUp = params.get('pickUp') || '';
  const dropOff = params.get('dropOff') || '';
  if (!location || !pickUp || !dropOff) return null;
  const pu = parseISO(pickUp);
  const doff = parseISO(dropOff);
  if (!isValid(pu) || !isValid(doff) || doff <= pu) return null;
  const validAges = ['21-24', '25-29', '30-69', '70+'];
  const rawAge = params.get('age') || '30-69';
  const driverAge = validAges.includes(rawAge) ? rawAge : '30-69';
  return { location, pickUp, dropOff, driverAge };
};

const CarHero = ({ canonical }) => (
  <section className="bg-teal-800 pt-8 pb-24 relative">
    <div className="max-w-7xl mx-auto px-4 md:px-6 text-white">
      <div className="text-teal-200 uppercase tracking-widest text-xs font-semibold">Car rentals</div>
      <h1 className="font-display font-extrabold text-2xl md:text-3xl" data-testid="cars-heading">
        {canonical ? canonical.location : 'Drive further, pay less'}
      </h1>
      <p className="text-white/80 text-sm mt-1">
        {canonical
          ? `${format(parseISO(canonical.pickUp), 'EEE, d MMM')} — ${format(parseISO(canonical.dropOff), 'EEE, d MMM')} · Driver ${canonical.driverAge}`
          : 'Compare Hertz, Avis, Sixt, Europcar and more · free cancellation'}
      </p>
    </div>
  </section>
);

const NoActiveProviderCard = ({ providersUnavailable }) => {
  const visible = (providersUnavailable || []).filter(p =>
    !/sandbox_disabled_in_production|kayak/i.test(`${p.reason || ''} ${p.name || ''}`)
  );
  return (
  <Card className="border-slate-200" data-testid="cars-no-provider">
    <CardContent className="p-10 md:p-14 text-center">
      <div className="w-14 h-14 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center mx-auto">
        <PlugZap className="w-7 h-7" />
      </div>
      <div className="font-display font-extrabold text-xl text-slate-900 mt-4">
        No active car rental provider available yet
      </div>
      <p className="text-slate-600 text-sm mt-2 max-w-lg mx-auto">
        We're finalising integrations with our car rental partners. Live vehicles and rates will
        appear here as soon as a provider is activated. No dummy cars are ever shown here.
      </p>
      {visible.length > 0 && (
        <div className="mt-5 inline-flex flex-wrap gap-2 justify-center">
          {visible.map((p, i) => (
            <Badge key={i} variant="outline" className="border-slate-300 text-slate-600 text-[11px]">
              {p.name}: {p.reason}
            </Badge>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
  );
};

const CarResultCard = ({ c, days }) => {
  const total = c.totalPrice ?? (c.pricePerDay != null ? c.pricePerDay * days : null);
  return (
    <Card className="overflow-hidden border-slate-200 card-lift" data-testid="car-card">
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
        {c.image
          ? <img src={c.image} alt={c.model} className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0 flex items-center justify-center text-slate-400"><Car className="w-10 h-10" /></div>
        }
        <Badge className="absolute top-3 left-3 bg-teal-700 text-white border-0">{c.category}</Badge>
      </div>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{c.vendor}</div>
        <div className="font-display font-bold text-slate-900 truncate" title={c.model}>{c.model}</div>
        <div className="flex flex-wrap gap-3 mt-3 text-sm text-slate-700">
          {c.seats != null && <span className="flex items-center gap-1"><Users className="w-4 h-4 text-slate-400" /> {c.seats}</span>}
          {c.bags != null && <span className="flex items-center gap-1"><Briefcase className="w-4 h-4 text-slate-400" /> {c.bags}</span>}
          {c.transmission && <span className="flex items-center gap-1"><Cog className="w-4 h-4 text-slate-400" /> {c.transmission}</span>}
        </div>
        <div className="flex items-end justify-between mt-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">per day</div>
            <div className="font-display font-extrabold text-xl text-teal-700">
              {c.pricePerDay != null ? `${c.currency} ${Math.round(c.pricePerDay)}` : '—'}
            </div>
            {total != null && (
              <div className="text-[11px] text-slate-500">Total {c.currency} {Math.round(total)}</div>
            )}
          </div>
          <a
            href={c.deeplink || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center h-10 px-4 rounded-md bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm"
            data-testid="car-view-deal"
          >
            Book now
          </a>
        </div>
        <div className="mt-2 text-[10px] text-slate-500">via {c.providerName}</div>
      </CardContent>
    </Card>
  );
};

export default function Cars() {
  const [params] = useSearchParams();
  const canonical = useMemo(() => readSearchFromParams(params), [params]);
  const [status, setStatus] = useState('idle');
  const [results, setResults] = useState([]);
  const [providers, setProviders] = useState({ used: [], unavailable: [] });
  const [sandbox, setSandbox] = useState(false);
  const [sort, setSort] = useState('recommended');
  const [maxPrice, setMaxPrice] = useState([2000]);
  const abortRef = useRef(null);

  const days = useMemo(() => {
    if (!canonical) return 1;
    return Math.max(1, differenceInCalendarDays(parseISO(canonical.dropOff), parseISO(canonical.pickUp)));
  }, [canonical]);

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    if (!canonical) {
      setStatus('idle');
      setResults([]);
      setProviders({ used: [], unavailable: [] });
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('loading');
    setResults([]);
    (async () => {
      try {
        const body = {
          pickupLocation: canonical.location,
          dropoffLocation: canonical.location,
          pickupDateTime: canonical.pickUp,
          dropoffDateTime: canonical.dropOff,
          driverAge: canonical.driverAge,
        };
        const { data } = await axios.post(`${API}/cars/search`, body, { signal: controller.signal });
        if (controller.signal.aborted) return;
        setResults(Array.isArray(data.results) ? data.results : []);
        setProviders(data.providers || { used: [], unavailable: [] });
        setSandbox(!!data.sandbox);
        setStatus(data.status === 'complete' ? 'complete' : 'no_provider');
      } catch (e) {
        if (controller.signal.aborted || axios.isCancel(e)) return;
        setStatus('error');
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonical?.location, canonical?.pickUp, canonical?.dropOff, canonical?.driverAge]);

  const filtered = useMemo(() => {
    let list = results.filter(r => r.pricePerDay == null || r.pricePerDay <= maxPrice[0]);
    if (sort === 'price') list = [...list].sort((a, b) => (a.pricePerDay ?? 9e9) - (b.pricePerDay ?? 9e9));
    return list;
  }, [results, maxPrice, sort]);

  return (
    <div>
      <CarHero canonical={canonical} />
      <div className="max-w-7xl mx-auto px-4 md:px-6 -mt-16 relative z-10">
        {/* Cheapora category nav — Flights routes back to homepage TP WL. */}
        <CategoryNav />
        <SearchWidget defaultTab="cars" compact initial={canonical} key={params.toString() || 'landing'} />
      </div>

      {!canonical && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 text-center">
          <Search className="w-8 h-8 text-teal-700 mx-auto" />
          <div className="font-display font-bold text-slate-900 mt-3">Start your car rental search</div>
          <p className="text-slate-600 text-sm mt-1 max-w-md mx-auto">
            Enter a pick-up location and dates above. We'll compare live vehicles across our
            activated provider partners.
          </p>
        </div>
      )}

      {canonical && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 grid grid-cols-1 lg:grid-cols-4 gap-6">
          <aside className="lg:col-span-1">
            <Card className="border-slate-200 sticky top-20">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Filter className="w-4 h-4 text-teal-700" />
                  <div className="font-display font-bold text-slate-900">Filters</div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Max price / day</Label>
                  <Slider value={maxPrice} onValueChange={setMaxPrice} min={20} max={3000} step={20} className="mt-3" data-testid="car-price-slider" />
                  <div className="text-sm mt-2 text-slate-700">Up to <span className="font-bold text-teal-700">S${maxPrice[0]}</span></div>
                </div>
                <Separator className="my-5" />
                <div className="text-xs text-slate-500 leading-relaxed">
                  Filters activate as soon as a car rental provider is connected. Nothing is
                  fabricated here in the meantime.
                </div>
              </CardContent>
            </Card>
          </aside>

          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="text-sm text-slate-600 flex items-center gap-2 flex-wrap">
                {status === 'loading' && (<><Loader2 className="w-4 h-4 animate-spin text-teal-700" /> Searching partner inventory…</>)}
                {status === 'complete' && (<>{filtered.length} of {results.length} vehicles found</>)}
                {status === 'no_provider' && (<>0 vehicles · No active car provider yet</>)}
                {status === 'error' && (<>Couldn't fetch vehicles right now.</>)}
              </div>
              <Tabs value={sort} onValueChange={setSort}>
                <TabsList>
                  <TabsTrigger value="recommended">Recommended</TabsTrigger>
                  <TabsTrigger value="price">Cheapest</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {status === 'loading' && (
              <Card className="border-slate-200">
                <CardContent className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-teal-700" /> Fetching partner inventory…
                </CardContent>
              </Card>
            )}
            {status === 'complete' && filtered.length > 0 && (
              <>
                <IndicativeResultsNotice />
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="cars-results-grid">
                  {filtered.map(c => <CarResultCard key={c.id} c={c} days={days} />)}
                </div>
              </>
            )}
            {status === 'complete' && filtered.length === 0 && (
              <Card className="border-slate-200" data-testid="cars-empty-filters">
                <CardContent className="p-10 text-center text-slate-500">
                  No vehicles match your filters. Try increasing the max price.
                </CardContent>
              </Card>
            )}
            {status === 'no_provider' && (
              <Card className="border-slate-200" data-testid="cars-no-provider">
                <CardContent className="p-10 md:p-14 text-center">
                  <div className="font-display font-extrabold text-xl text-slate-900">
                    No indicative results are currently available for this location
                  </div>
                  <p className="text-slate-600 text-sm mt-2 max-w-lg mx-auto">
                    Please try another pick-up location or date. Live booking availability is
                    coming soon as we finalise our provider integrations.
                  </p>
                </CardContent>
              </Card>
            )}
            {status === 'error' && (
              <Card className="border-slate-200">
                <CardContent className="p-10 text-center text-slate-500">
                  We couldn't fetch vehicles right now. Please try again in a moment.
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
